import React from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { ChevronLeft, ChevronRight, Menu } from "lucide-react";
import { Button } from "@/components/ui/button";
import MarstickLogoMark from "@/components/brand/MarstickLogoMark";
import { getLangFromPath, SUPPORTED_LANGUAGES } from "@/lib/i18nRoutes";
import { useLanguage } from "@/lib/LanguageContext";
import { getMobileNavigationStackDepth, getMobilePageTitle } from "@/lib/mobileNavigation";

const PublicAuthButton = React.lazy(() => import("@/components/public/PublicAuthButton"));

const headerCopy = {
  en: { back: "Back", menu: "Menu", subtitle: "Smarter sailing companion", home: "Marstick home", openMenu: "Open menu" },
  he: { back: "חזרה", menu: "תפריט", subtitle: "עוזר שייט חכם", home: "בית Marstick", openMenu: "פתח תפריט" },
  es: { back: "Atrás", menu: "Menú", subtitle: "Compañero náutico inteligente", home: "Inicio de Marstick", openMenu: "Abrir menú" },
  it: { back: "Indietro", menu: "Menu", subtitle: "Compagno di navigazione smart", home: "Home Marstick", openMenu: "Apri menu" },
  fr: { back: "Retour", menu: "Menu", subtitle: "Compagnon nautique intelligent", home: "Accueil Marstick", openMenu: "Ouvrir le menu" },
  de: { back: "Zurück", menu: "Menü", subtitle: "Smarter Segelbegleiter", home: "Marstick Startseite", openMenu: "Menü öffnen" },
  pt: { back: "Voltar", menu: "Menu", subtitle: "Companheiro náutico inteligente", home: "Início Marstick", openMenu: "Abrir menu" },
  ar: { back: "رجوع", menu: "القائمة", subtitle: "رفيق إبحار ذكي", home: "صفحة Marstick الرئيسية", openMenu: "افتح القائمة" },
  el: { back: "Πίσω", menu: "Μενού", subtitle: "Έξυπνος σύντροφος πλεύσης", home: "Αρχική Marstick", openMenu: "Άνοιγμα μενού" },
  ru: { back: "Назад", menu: "Меню", subtitle: "Умный помощник в плавании", home: "Главная Marstick", openMenu: "Открыть меню" },
  tr: { back: "Geri", menu: "Menü", subtitle: "Akıllı seyir yardımcısı", home: "Marstick ana sayfa", openMenu: "Menüyü aç" },
  zh: { back: "返回", menu: "菜单", subtitle: "智能航海助手", home: "Marstick 首页", openMenu: "打开菜单" },
};

export default function MobileHeader() {
  const location = useLocation();
  const navigate = useNavigate();
  const { language } = useLanguage();
  const [showAuth, setShowAuth] = React.useState(false);
  const pathLang = location.pathname.split("/").filter(Boolean)[0];
  const lang = SUPPORTED_LANGUAGES.includes(pathLang) ? pathLang : language || getLangFromPath(location.pathname) || "en";
  const copy = headerCopy[lang] || {};
  const stackDepth = getMobileNavigationStackDepth(location.pathname, lang);
  const isRTL = lang === "he" || lang === "ar";
  const title = getMobilePageTitle(location.pathname, lang);
  const menuPath = lang === "en" ? "/menu" : `/${lang}/menu`;
  const BackIcon = isRTL ? ChevronRight : ChevronLeft;

  React.useEffect(() => {
    const show = () => setShowAuth(true);
    const idleId = window.requestIdleCallback ? window.requestIdleCallback(show, { timeout: 3000 }) : window.setTimeout(show, 1800);
    return () => {
      if (window.cancelIdleCallback) window.cancelIdleCallback(idleId);
      else window.clearTimeout(idleId);
    };
  }, []);


  return (
    <>
      <header
        className="fixed z-[95] flex min-h-14 select-none items-center rounded-[1.35rem] border border-white/80 bg-white px-2 py-1.5 text-slate-950 shadow-xl shadow-ocean/10 ring-1 ring-slate-200/80 backdrop-blur-2xl dark:border-white/10 dark:bg-slate-950 dark:text-white dark:ring-white/10 md:hidden"
        style={{ top: "calc(0.5rem + env(safe-area-inset-top, 0px))", left: "max(0.5rem, env(safe-area-inset-left, 0px))", right: "max(0.5rem, env(safe-area-inset-right, 0px))" }}
      >
        <div className="flex w-full items-center gap-1.5">
          {stackDepth > 0 ? (
            <Button variant="ghost" onClick={() => navigate(-1)} className="h-10 shrink-0 rounded-full bg-slate-950 px-3 text-white shadow-lg shadow-slate-900/20 hover:bg-slate-800" aria-label={copy.back}>
              <BackIcon className="h-5 w-5" aria-hidden="true" />
              <span className="text-sm font-bold">{copy.back}</span>
            </Button>
          ) : (
            <Link to={lang === "en" ? "/" : `/${lang}`} className="flex shrink-0 items-center gap-2 rounded-2xl text-slate-950 focus-visible:ring-2 focus-visible:ring-primary/45 dark:text-white" aria-label={copy.home}>
              <MarstickLogoMark className="h-10 w-10 rounded-xl shadow-lg shadow-primary/20" />
              <span className="font-heading text-sm font-black leading-none">Marstick</span>
            </Link>
          )}
          <div className="min-w-0 flex-1">
            <h1 className="line-clamp-1 font-heading text-[13px] font-bold leading-tight text-slate-950 dark:text-white">{title || "Marstick"}</h1>
            <div className="mt-0.5 min-h-5 overflow-hidden">
              {showAuth && (
                <React.Suspense fallback={null}>
                  <PublicAuthButton showMobileLabel />
                </React.Suspense>
              )}
            </div>
          </div>
          <div className="flex shrink-0 items-center gap-1">
            <Button variant="ghost" onClick={() => navigate(menuPath)} className="h-10 shrink-0 gap-1.5 rounded-full bg-slate-950 px-3 text-white shadow-lg shadow-slate-900/20 ring-1 ring-slate-900 hover:bg-slate-800 dark:bg-slate-950 dark:text-white dark:ring-slate-900 dark:hover:bg-slate-800" aria-label={copy.openMenu}>
              <Menu className="h-5 w-5" aria-hidden="true" />
              <span className="text-sm font-bold">{copy.menu}</span>
            </Button>
          </div>
        </div>
      </header>

    </>
  );
}